import { resolveId } from '@medplum/core'
import type {
  Appointment,
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import * as appointments from '@seen/core/models/appointments/index'
import { getReferenceString } from '@seen/core/references'
import { normalizeQuery } from '@seen/core/search'
import {
  type QueriesOptions,
  type UseQueryResult,
  useQueries,
} from '@tanstack/react-query'
import { compact } from 'lodash-es'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useDebounced } from '#src/hooks/useDebounced.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface UseAppointmentsOptions {
  start: string
  end: string
  practitioners?: Reference<Practitioner>[]
  locations?: Reference<Location>[]
  patients?: Reference<Patient>[]
  healthcareServices?: Reference<HealthcareService>[]
  statuses?: Appointment['status'][]
  ignoreBusy?: boolean
}

export type UseAppointmentsResult = UseQueryResult<
  appointments.ExtendedAppointment[]
> & {
  // True while any data feeding a given filter entity is still fetching, keyed
  // by the entity's reference string. A practitioner is loading while either
  // its gcal events or the Medplum search are fetching; every other entity
  // reflects the (global) Medplum search. Keys are only present for entities in
  // the (debounced) options, so an absent key reads as undefined.
  isFetchingByEntity: Record<string, boolean | undefined>
}

export function useAppointments(
  options: UseAppointmentsOptions
): UseAppointmentsResult {
  const medplum = useMedplum()
  const { seenApi } = useSeenApi()

  // Debounce options to avoid rapid requests when filters change
  const memoizedOptions = useMemoValue(options)
  const debouncedOptions = useDebounced(memoizedOptions, 300)

  const parameters = normalizeQuery({ ...debouncedOptions })

  const practitionerIds = compact(
    debouncedOptions.practitioners?.map(resolveId)
  )
  const externalAppointmentsResult = useQueries({
    queries: practitionerIds.map(
      (practitionerId) =>
        ({
          queryKey: [
            'appointments-external',
            practitionerId,
            debouncedOptions.start,
            debouncedOptions.end,
          ],
          staleTime: 60 * 1000,
          queryFn: async () => {
            if (!debouncedOptions.end || !debouncedOptions.start) {
              return []
            }

            const calendarEvents = await seenApi.post(
              '/practitioner-calendar-events',
              {
                practitionerId,
                startTime: debouncedOptions.start,
                endTime: debouncedOptions.end,
              }
            )
            const result =
              calendarEvents.data.appointments?.map((a: Appointment) =>
                appointments.ExtendedAppointment.fromFhirResource(a)
              ) || []
            return result as appointments.ExtendedAppointment[]
          },
        }) satisfies QueriesOptions<appointments.ExtendedAppointment[]>[number]
    ),
  })

  const medplumAppointmentsResult = useSearch(
    appointments.ExtendedAppointment,
    parameters,
    {
      query: {
        queryFn: async () => {
          const medplumAppointments = await appointments.getAppointments(
            medplum,
            debouncedOptions
          )

          return medplumAppointments.map((a) =>
            appointments.ExtendedAppointment.fromFhirResource(a)
          )
        },
      },
    }
  )

  const allAppointments = useMemoValue([
    ...externalAppointmentsResult.flatMap((r) => r.data ?? []),
    ...(medplumAppointmentsResult.data ?? []),
  ])

  const medplumFetching = medplumAppointmentsResult.isFetching
  // gcal events load per practitioner, keyed positionally to practitionerIds.
  const gcalFetchingByPractitionerId: Record<string, boolean> =
    Object.fromEntries(
      practitionerIds.map((id, i) => [
        id,
        externalAppointmentsResult[i]?.isFetching ?? false,
      ])
    )

  const isFetchingByEntity = useMemoValue(
    Object.fromEntries(
      [
        ...(debouncedOptions.practitioners ?? []),
        ...(debouncedOptions.locations ?? []),
        ...(debouncedOptions.patients ?? []),
        ...(debouncedOptions.healthcareServices ?? []),
      ].map((ref) => {
        const gcalFetching =
          gcalFetchingByPractitionerId[resolveId(ref) ?? ''] ?? false
        return [getReferenceString(ref), medplumFetching || gcalFetching]
      })
    )
  )

  return {
    ...medplumAppointmentsResult,
    data: allAppointments,
    isFetchingByEntity,
  } as UseAppointmentsResult
}
